import { JSX, Component, PropsWithChildren, ErrorInfo, ReactNode } from 'react';
import { envConfig } from '@bg-shared/constants/envConfig';
import { refreshIframe } from '@bg-shared/utils/refreshIframe';
import { partnerSettingsStore } from '@bg-shared/business/PartnerSettings';
import { webAPIIntegration } from '@bg-shared/integrations/WebIntegration/webAPIIntegration';
import { useHistory } from './useHistory';
import { withRouter } from './withRouter';
import { ErrorName, ErrorCode } from './enums';
import { ErrorBoundaryContext } from './ErrorBoundaryContext';

interface IError extends Error {
    code: string;
}

interface IErrorComponentProps extends Record<string, unknown> {
    message: string;
}

interface IProps {
    ErrorComponent: (props: IErrorComponentProps) => JSX.Element;
    history?: ReturnType<typeof useHistory>;
}

interface IState {
    error: string;
    chunkError: boolean;
}

class ErrorBoundaryComponent extends Component<PropsWithChildren<IProps>, IState> {
    static getDerivedStateFromError(error: IError): IState {
        return {
            error:
                (envConfig.isTestingEnv || envConfig.isIntegrationsEnv) && error?.message
                    ? error.message
                    : `#${
                          error?.code ?? ''
                      }-${error?.name}. Something unexpected happened, try to refresh the page`,
            chunkError: error?.name === ErrorName.chunk || error?.code === ErrorCode.cssChunk,
        };
    }

    unsubscribe: () => void;

    constructor(props: IProps) {
        super(props);

        this.state = {
            error: null,
            chunkError: false,
        };
    }

    componentDidMount(): void {
        this.unsubscribe = this.props.history.listen(() => {
            if (this.state.error) {
                this.setState({ error: null, chunkError: false });
            }
        });
    }

    componentWillUnmount(): void {
        this.unsubscribe();
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        if (this.state.chunkError) {
            refreshIframe();
        } else {
            console.error(error, errorInfo);
            webAPIIntegration(partnerSettingsStore.webIntegrationType, (api) => {
                api.errorMessage(error?.message);
            });
        }
    }

    triggerError = (error: Error): void => {
        console.error(error);
        webAPIIntegration(partnerSettingsStore.webIntegrationType, (api) => {
            api.errorMessage(error?.message);
        });

        this.setState({ error: error.message });
    };

    renderBoundary(): ReactNode {
        if (this.state.chunkError) {
            return null;
        }

        if (this.state.error) {
            const { ErrorComponent } = this.props;
            return <ErrorComponent message={this.state.error} />;
        }

        return this.props.children;
    }

    render(): ReactNode {
        return (
            <ErrorBoundaryContext.Provider value={this.triggerError}>
                {this.renderBoundary()}
            </ErrorBoundaryContext.Provider>
        );
    }
}

export const ErrorBoundary = withRouter(ErrorBoundaryComponent);
